import {
  Button,
  Flex,
  useDisclosure,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import React, { createContext, useContext, useMemo, useState } from 'react';

import { RichTextEditor } from '~/packages/common-frontend/components/RichTextEditor/RichTextEditor';

// Types
type CommentEditorContextValue = {
  hideActionsWhenEmpty: boolean;
  isDisabled: boolean;
  disclosure: UseDisclosureReturn;
  comment: string;
  setComment: (nextComment: string) => void;
  onCreate: (newComment: string) => void;
  onCancel: () => void;
};

// Constants
const defaultContextValue: CommentEditorContextValue = {
  hideActionsWhenEmpty: true,
  isDisabled: false,
  disclosure: {
    isOpen: false,
    onOpen: () => undefined,
    onClose: () => undefined,
    onToggle: () => undefined,
    isControlled: false,
    getButtonProps: () => undefined,
    getDisclosureProps: () => undefined,
  },
  comment: '',
  setComment: () => undefined,
  onCreate: () => undefined,
  onCancel: () => undefined,
};

// Contexts
export const CommentEditorContext = createContext(defaultContextValue);

// Hooks
export const useCommentEditorContext = () => useContext(CommentEditorContext);

// Fragments
type CommentEditorActionsProps = {
  left?: React.ReactNode;
  right?: React.ReactNode;
};
const CommentEditorActions = ({ left, right }: CommentEditorActionsProps) => {
  const {
    comment,
    disclosure,
    hideActionsWhenEmpty,
  } = useCommentEditorContext();

  if (!left && !right) return null;
  if (!disclosure.isOpen && comment.length === 0 && hideActionsWhenEmpty) {
    return null;
  }

  return (
    <Flex
      gap="0.75rem"
      flexWrap="wrap"
      flexDirection={['column', 'column', 'row']}
    >
      {left && (
        <Flex
          justifyContent={['center', 'center', 'flex-start']}
          flexGrow={1}
          gap="0.75rem"
          overflow="hidden"
        >
          {left}
        </Flex>
      )}
      {right && (
        <Flex
          justifyContent={['center', 'center', 'flex-end']}
          flexGrow={1}
          gap="0.75rem"
          overflow="hidden"
        >
          {right}
        </Flex>
      )}
    </Flex>
  );
};

// Exports
type CommentEditorInputProps = Omit<
  React.ComponentProps<typeof RichTextEditor>,
  'text' | 'setText'
>;
export const CommentEditorInput = ({
  placeHolderText = 'Leave a comment...',
  ...props
}: CommentEditorInputProps) => {
  const { comment, setComment } = useCommentEditorContext();
  return (
    <RichTextEditor
      {...props}
      text={comment}
      setText={setComment}
      placeHolderText={placeHolderText}
    />
  );
};

type CommentEditorCancelButtonProps = React.ComponentProps<typeof Button>;
export const CommentEditorCancelButton = ({
  children = 'Cancel',
  colorScheme = 'v2-aurora',
  size = 'sm',
  variant = 'secondary',
  ...props
}: CommentEditorCancelButtonProps) => {
  const {
    comment,
    onCancel,
    setComment,
    isDisabled,
  } = useCommentEditorContext();
  return (
    <Button
      flexShrink={0}
      size={size}
      variant={variant}
      colorScheme={colorScheme}
      isDisabled={isDisabled || comment.length === 0}
      {...props}
      onClick={async () => {
        await onCancel();
        setComment('');
      }}
    >
      {children}
    </Button>
  );
};

type CommentEditorCreateButtonProps = React.ComponentProps<typeof Button>;
export const CommentEditorCreateButton = ({
  children = 'Reply',
  size = 'sm',
  variant = 'primary',
  colorScheme = 'v2-aurora',
  ...props
}: CommentEditorCreateButtonProps) => {
  const {
    comment,
    setComment,
    onCreate,
    isDisabled,
  } = useCommentEditorContext();
  return (
    <Button
      flexShrink={0}
      size={size}
      variant={variant}
      colorScheme={colorScheme}
      isDisabled={isDisabled || comment.length === 0}
      {...props}
      onClick={async () => {
        await onCreate(comment);
        setComment('');
      }}
    >
      {children}
    </Button>
  );
};

type CommentEditorProps = {
  children: React.ReactNode;
  hideActionsWhenEmpty?: boolean;
  isDisabled?: boolean;
  initialComment?: string;
  left?: React.ReactNode;
  right?: React.ReactNode;
  onCreate?: (newComment: string) => void;
  onCancel?: () => void;
};
export const CommentEditor = ({
  children,
  hideActionsWhenEmpty = true,
  isDisabled = false,
  initialComment = '',
  left,
  right,
  onCreate,
  onCancel,
}: CommentEditorProps) => {
  const [comment, setComment] = useState<string>(initialComment);
  const disclosure = useDisclosure({ defaultIsOpen: false });
  const contextValue = useMemo(
    () => ({
      hideActionsWhenEmpty,
      isDisabled,
      disclosure,
      comment,
      setComment,
      onCreate: onCreate ?? (() => undefined),
      onCancel: onCancel ?? (() => undefined),
    }),
    [comment, disclosure, hideActionsWhenEmpty, isDisabled, onCancel, onCreate],
  );
  return (
    <CommentEditorContext.Provider value={contextValue}>
      <Flex
        width="full"
        direction="column"
        gap="0.75rem"
        onFocus={() => disclosure.onOpen()}
        onBlur={() => disclosure.onClose()}
      >
        {children}
        <CommentEditorActions left={left} right={right} />
      </Flex>
    </CommentEditorContext.Provider>
  );
};
